import {
  useBoundProp,
  useCardSession,
  useCardSessionControls,
  useCardStatus,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { withAskUserFreeText, withAskUserPick } from '@littlebird/sdk';
import { Fragment } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import {
  FreeTextRow,
  OptionRow,
  RowDivider,
} from '@/features/ask-user/AskUserRows';

import { StackedField } from '../field';
import { labelIdOf, useElementId } from '../fieldIds';
import { sizingClass } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

// An ask_user question's options on a card, drawn with the in-chat picker's rows
export const OptionList: ComponentFn<typeof catalog, 'OptionList'> = ({
  props,
  bindings,
  on,
}) => {
  const id = useElementId(props);
  const [picked, setPicked] = useBoundProp<string[]>(
    props.value,
    bindings?.value
  );
  const [freeText, setFreeText] = useBoundProp<string | undefined>(
    props.freeText,
    bindings?.freeText
  );
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const valuePath = bindings?.value;
  const freeTextPath = bindings?.freeText;
  const pickEvent = on('pick');
  const isRunning = status === 'running';
  // A literal value has no state path to write to, so it cannot be picked
  const editable =
    valuePath !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !isRunning;
  const allowMultiple = props.allowMultiple ?? false;
  const selected = picked ?? [];

  const answer = { selected, freeText: freeText ?? '' };

  // The picker's rules, so one answer is a pick or the user's own words, not both
  // An edit clears the server's refusal of the field, as other inputs' edits do
  const pick = (option: string) => {
    const wasSelected = selected.includes(option);
    const next = withAskUserPick(answer, option, allowMultiple);
    setPicked([...next.selected]);
    if (valuePath) controls.fieldChanged(valuePath);
    if (next.freeText !== answer.freeText) {
      setFreeText(next.freeText);
      if (freeTextPath) controls.fieldChanged(freeTextPath);
    }
    if (!allowMultiple && !wasSelected && pickEvent.bound) {
      pickEvent.emit();
    } else {
      controls.commitNow();
    }
  };

  const typeFreeText = (text: string) => {
    const next = withAskUserFreeText(answer, text, allowMultiple);
    setFreeText(next.freeText);
    if (freeTextPath) controls.fieldChanged(freeTextPath);
    // Typing replaced the pick
    if (next.selected.length !== answer.selected.length) {
      setPicked([...next.selected]);
      if (valuePath) controls.fieldChanged(valuePath);
    }
  };

  const field = (
    <StackedField
      id={id}
      label={props.label}
      labelsControl={false}
      labelHidden={props.labelHidden}
      error={props.error}
    >
      <YStack
        id={id}
        role="group"
        aria-labelledby={props.label ? labelIdOf(id) : undefined}
      >
        {props.options.map((option, index) => (
          <Fragment key={option}>
            {index > 0 && <RowDivider />}
            <OptionRow
              number={index + 1}
              label={option}
              isSelected={selected.includes(option)}
              multiSelect={allowMultiple}
              disabled={!editable}
              onClick={() => pick(option)}
            />
          </Fragment>
        ))}
        {props.allowFreeText !== false && (
          <>
            {props.options.length > 0 && <RowDivider />}
            <FreeTextRow
              value={freeText ?? ''}
              multiSelect={allowMultiple}
              disabled={!editable || freeTextPath === undefined}
              placeholder={props.placeholder}
              onChange={typeFreeText}
              onFocus={() => freeTextPath && controls.focus(freeTextPath)}
              onBlur={() => freeTextPath && controls.blur(freeTextPath)}
            />
          </>
        )}
      </YStack>
    </StackedField>
  );

  return props.sizing === 'fill' ? (
    <div className={sizingClass(props.sizing)}>{field}</div>
  ) : (
    field
  );
};
